import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiErrorMessage } from "../../services/apiError";
import UserAvatar from "./UserAvatar";
import { AVATAR_MAX_BYTES, AVATAR_TYPES } from "./avatarRules";

/**
 * Set or remove a profile picture (photos-and-avatars D8): choose a file,
 * see it as the circle everyone will see — the middle square, exactly what
 * the server keeps — then Save or choose another. Used for your own picture
 * (header) and for an agent's (its page, staff or its owner).
 *
 * `onSave(file)` / `onRemove()` do the API call and apply its result;
 * a rejection shows here in the API's words and the dialog stays open.
 */
function AvatarDialog({
  open,
  onOpenChange,
  user,
  title,
  whose,
  onSave,
  onRemove,
  returnFocusTo = null,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(null); // "save" | "remove" | null
  const inputRef = useRef(null);

  // A fresh start each time it opens; the object URL is freed with it.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setProblem(null);
      setBusy(null);
    }
  }, [open]);
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const choose = (event) => {
    const picked = event.target.files?.[0];
    event.target.value = ""; // choosing the same file again still fires
    if (!picked) return;
    if (!AVATAR_TYPES.includes(picked.type)) {
      setProblem("Choose a PNG, JPEG, WebP or GIF image.");
      return;
    }
    if (picked.size > AVATAR_MAX_BYTES) {
      setProblem("That image is over 10 MB — choose a smaller one.");
      return;
    }
    setProblem(null);
    setFile(picked);
  };

  const run = async (kind, action) => {
    setBusy(kind);
    setProblem(null);
    try {
      await action();
      onOpenChange(false);
    } catch (err) {
      setProblem(
        apiErrorMessage(
          err,
          kind === "save"
            ? "Couldn't save the picture."
            : "Couldn't remove the picture.",
        ),
      );
    } finally {
      setBusy(null);
    }
  };

  const hasPicture = Boolean(user?.avatar_url);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent
        className="sm:max-w-sm"
        // A static backdrop, like the app's other dialogs: a stray click
        // outside mustn't throw away the chosen picture.
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => {
          // Opened from a menu item, the opener unmounted with its menu:
          // ui/dialog.jsx would hand focus to <body>. The caller passes
          // where it should land instead (as MintAgentModal does).
          if (returnFocusTo?.current) {
            event.preventDefault();
            returnFocusTo.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {`Shown next to ${whose} name wherever it appears — on the board and in task threads. The middle square is kept: this circle is exactly what everyone will see.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3 py-2">
          {preview ? (
            <img
              src={preview}
              alt={`${whose[0].toUpperCase()}${whose.slice(1)} new picture, as it will appear`}
              className="size-32 rounded-full border object-cover"
            />
          ) : (
            <UserAvatar user={user} size="lg" className="size-32 text-4xl" />
          )}
          <input
            ref={inputRef}
            type="file"
            accept={AVATAR_TYPES.join(",")}
            className="sr-only"
            // The button below is the one control; this input is only its
            // engine (not focusable, not announced twice).
            tabIndex={-1}
            aria-hidden="true"
            aria-label="Choose a picture"
            onChange={choose}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={Boolean(busy)}
            onClick={() => inputRef.current?.click()}
          >
            {file ? "Choose another" : "Choose a picture"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            PNG, JPEG, WebP or GIF, up to 10 MB. Location and other photo data
            are removed.
          </p>
          {problem && (
            <p role="alert" className="text-center text-sm text-destructive">
              {problem}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {hasPicture && !file ? (
            <Button
              type="button"
              variant="ghost"
              disabled={Boolean(busy)}
              onClick={() => run("remove", onRemove)}
            >
              {busy === "remove" ? "Removing…" : "Remove picture"}
            </Button>
          ) : (
            <span />
          )}
          <Button
            type="button"
            disabled={!file || Boolean(busy)}
            onClick={() => run("save", () => onSave(file))}
          >
            {busy === "save" ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

AvatarDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  user: PropTypes.shape({
    username: PropTypes.string,
    kind: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
  /** "your" / "scout's" — for the description sentence. */
  whose: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  /** Where focus goes on close, when the opener won't be there. */
  returnFocusTo: PropTypes.shape({ current: PropTypes.any }),
};

export default AvatarDialog;
